import {
  AlertTriangle,
  Check,
  Loader2,
  Shield,
  Sparkles,
  X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatConfidencePct, humanizeFieldValue } from './diff'
import {
  actionHeadline,
  isFieldEdit,
  isProjectMetadata,
  isProjectTask,
  type AiActionState,
  type AiSuggestionAction,
} from './types'

export interface AiActionCardProps {
  action: AiSuggestionAction
  state: AiActionState
  selected: boolean
  selectable: boolean
  /** Hide the checkbox column entirely — useful for single-action contexts. */
  hideSelection?: boolean
  onToggleSelected: () => void
  onAccept: () => void
  onReject: () => void
  /** Optional override apply — the caller can offer a "Apply anyway" path. */
  onForceAccept?: () => void
}

/**
 * One AI suggestion rendered as an explicit action card. The card NEVER
 * auto-applies — every status transition out of `pending` is gated by an
 * explicit user click (Apply / Dismiss / Apply anyway).
 *
 * Status surface:
 *  - pending  → Apply + Dismiss visible
 *  - applying → spinner, both controls disabled
 *  - applied  → success chip, no controls (terminal)
 *  - rejected → muted chip, no controls (terminal)
 *  - failed   → error message + retry / dismiss
 *  - denied   → permission warning + dismiss only
 *  - conflict → "value changed" warning + Apply anyway + Cancel
 */
export function AiActionCard({
  action,
  state,
  selected,
  selectable,
  hideSelection = false,
  onToggleSelected,
  onAccept,
  onReject,
  onForceAccept,
}: AiActionCardProps) {
  const isTerminal = state.status === 'applied' || state.status === 'rejected'
  const isApplying = state.status === 'applying'

  return (
    <li
      data-testid="ai-action-card"
      data-action-id={action.id}
      data-action-kind={action.kind}
      data-action-status={state.status}
      className={cn(
        'flex items-stretch gap-3 rounded-md border bg-card p-3 shadow-soft transition-colors',
        state.status === 'pending' && 'border-warning-soft/80 bg-warning-soft/30',
        state.status === 'applied' && 'border-success-soft bg-success-soft/30',
        state.status === 'rejected' && 'opacity-60',
        state.status === 'failed' && 'border-destructive/40 bg-destructive/5',
        state.status === 'denied' && 'border-destructive/40 bg-destructive/5',
        state.status === 'conflict' && 'border-warning bg-warning/5',
      )}
    >
      {!hideSelection && (
        <div className="flex shrink-0 items-start pt-0.5">
          <label className="inline-flex items-center" title="Include in batch">
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelected}
              disabled={!selectable || isTerminal || isApplying}
              data-testid="ai-action-checkbox"
              className="size-4 cursor-pointer rounded border-input accent-primary disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`Select ${actionHeadline(action)}`}
            />
          </label>
        </div>
      )}

      <div className="min-w-0 flex-1 space-y-2">
        <ActionHeader action={action} state={state} />
        <ActionDiff action={action} />
        <ContextRow action={action} />
        <StatusFooter
          action={action}
          state={state}
          onAccept={onAccept}
          onReject={onReject}
          onForceAccept={onForceAccept}
        />
      </div>
    </li>
  )
}

function ActionHeader({
  action,
  state,
}: {
  action: AiSuggestionAction
  state: AiActionState
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 space-y-0.5">
        <div className="flex items-center gap-1.5">
          <span className="aurora-eyebrow text-[10px]" data-testid="ai-action-kind">
            {kindLabel(action)}
          </span>
          {state.status === 'pending' && (
            <Badge tone="warning" size="xs">
              <Sparkles aria-hidden /> Awaiting review
            </Badge>
          )}
          {state.status === 'applied' && (
            <Badge tone="success" size="xs" data-testid="ai-action-applied-badge">
              <Check aria-hidden /> Applied
            </Badge>
          )}
          {state.status === 'rejected' && (
            <Badge tone="neutral" size="xs" data-testid="ai-action-rejected-badge">
              Dismissed
            </Badge>
          )}
          {state.status === 'applying' && (
            <Badge tone="info" size="xs">
              <Loader2 className="animate-spin" aria-hidden /> Applying…
            </Badge>
          )}
          {state.status === 'failed' && (
            <Badge tone="danger" size="xs" data-testid="ai-action-failed-badge">
              <AlertTriangle aria-hidden /> Failed
            </Badge>
          )}
          {state.status === 'denied' && (
            <Badge tone="danger" size="xs" data-testid="ai-action-denied-badge">
              <Shield aria-hidden /> Not allowed
            </Badge>
          )}
          {state.status === 'conflict' && (
            <Badge tone="warning" size="xs" data-testid="ai-action-conflict-badge">
              <AlertTriangle aria-hidden /> Value changed
            </Badge>
          )}
        </div>
        <div className="truncate text-sm font-medium text-foreground">
          {actionHeadline(action)}
        </div>
      </div>
    </div>
  )
}

function ActionDiff({ action }: { action: AiSuggestionAction }) {
  if (isFieldEdit(action)) {
    const before = humanizeFieldValue(action.before, action.fieldKind)
    const after = humanizeFieldValue(action.after, action.fieldKind)
    return (
      <DiffRow
        beforeText={before.text}
        beforeEmpty={before.isEmpty}
        afterText={after.text}
        afterEmpty={after.isEmpty}
      />
    )
  }

  if (isProjectMetadata(action)) {
    const rows: { label: string; before: string; after: string }[] = []
    if (action.changes.name) {
      rows.push({
        label: 'Name',
        before: action.changes.name.before,
        after: action.changes.name.after,
      })
    }
    if (action.changes.description) {
      rows.push({
        label: 'Description',
        before: action.changes.description.before ?? '',
        after: action.changes.description.after ?? '',
      })
    }
    return (
      <div className="space-y-1.5" data-testid="ai-action-diff">
        {rows.map((row) => (
          <div key={row.label} className="space-y-0.5">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {row.label}
            </div>
            <DiffRow
              beforeText={row.before || 'Empty'}
              beforeEmpty={!row.before}
              afterText={row.after || 'Empty'}
              afterEmpty={!row.after}
            />
          </div>
        ))}
      </div>
    )
  }

  if (isProjectTask(action)) {
    return (
      <div
        className="rounded-md border border-dashed border-border bg-background/60 p-2 text-xs text-muted-foreground"
        data-testid="ai-action-diff"
      >
        <span className="font-medium text-foreground">New task</span>
        {action.taskDescription ? (
          <p className="mt-0.5 whitespace-pre-wrap leading-relaxed">
            {action.taskDescription}
          </p>
        ) : null}
      </div>
    )
  }

  return null
}

function DiffRow({
  beforeText,
  beforeEmpty,
  afterText,
  afterEmpty,
}: {
  beforeText: string
  beforeEmpty: boolean
  afterText: string
  afterEmpty: boolean
}) {
  return (
    <div
      className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-2"
      data-testid="ai-action-diff-row"
    >
      <div
        className={cn(
          'rounded-md border bg-background px-2 py-1 sm:min-h-[1.75rem]',
          beforeEmpty && 'italic text-muted-foreground',
        )}
        data-testid="ai-action-diff-before"
      >
        <span className="block max-h-24 overflow-hidden whitespace-pre-wrap break-words">
          {beforeText}
        </span>
      </div>
      <span
        aria-hidden
        className="hidden text-muted-foreground sm:inline"
      >
        →
      </span>
      <div
        className={cn(
          'rounded-md border border-primary/40 bg-primary-soft/40 px-2 py-1 font-medium text-foreground sm:min-h-[1.75rem]',
          afterEmpty && 'italic text-muted-foreground',
        )}
        data-testid="ai-action-diff-after"
      >
        <span className="block max-h-24 overflow-hidden whitespace-pre-wrap break-words">
          {afterText}
        </span>
      </div>
    </div>
  )
}

function ContextRow({ action }: { action: AiSuggestionAction }) {
  const { confidence, reasoning, source, runId } = action.context
  const pct = formatConfidencePct(confidence)
  const lowConfidence = typeof confidence === 'number' && confidence < 0.6
  const showRow = pct != null || reasoning || source
  if (!showRow) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
      {source && (
        <Badge tone="outline" size="xs">
          {source}
        </Badge>
      )}
      {pct && (
        <Badge
          tone={lowConfidence ? 'warning' : 'outline'}
          size="xs"
          data-testid="ai-action-confidence"
        >
          {lowConfidence ? 'Low confidence' : 'Confidence'} ({pct})
        </Badge>
      )}
      {reasoning && (
        <span
          className="line-clamp-2 min-w-0 flex-1 italic"
          data-testid="ai-action-reasoning"
          title={reasoning}
        >
          {reasoning}
        </span>
      )}
      {runId && (
        <span
          aria-hidden
          className="ml-auto hidden font-mono text-[10px] text-muted-foreground/70 sm:inline"
          title={runId}
        >
          #{runId.slice(0, 8)}
        </span>
      )}
    </div>
  )
}

function StatusFooter({
  action,
  state,
  onAccept,
  onReject,
  onForceAccept,
}: {
  action: AiSuggestionAction
  state: AiActionState
  onAccept: () => void
  onReject: () => void
  onForceAccept?: () => void
}) {
  if (state.status === 'applied' || state.status === 'rejected') {
    return null
  }

  if (state.status === 'conflict') {
    return (
      <div
        className="space-y-1.5 rounded-md border border-warning/50 bg-warning/10 p-2 text-xs"
        data-testid="ai-action-conflict-panel"
      >
        <p className="font-medium text-warning-foreground">
          {state.message ?? 'Value changed since this suggestion was made.'}
        </p>
        {state.conflictCurrent != null && (
          <p className="text-muted-foreground">
            Current value:{' '}
            <span className="font-mono text-foreground">
              {state.conflictCurrent || '(empty)'}
            </span>
          </p>
        )}
        <div className="flex gap-1.5 pt-1">
          <Button
            size="sm"
            variant="default"
            onClick={onForceAccept ?? onAccept}
            data-testid="ai-action-force-accept"
          >
            Apply anyway
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            data-testid="ai-action-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  if (state.status === 'denied') {
    return (
      <div
        className="space-y-1.5 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs"
        data-testid="ai-action-denied-panel"
      >
        <p className="font-medium text-destructive">
          {state.message ?? 'You do not have permission to apply this change.'}
        </p>
        <div className="flex gap-1.5 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            data-testid="ai-action-dismiss"
          >
            Dismiss
          </Button>
        </div>
      </div>
    )
  }

  if (state.status === 'failed') {
    return (
      <div
        className="space-y-1.5 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs"
        data-testid="ai-action-failed-panel"
      >
        <p className="font-medium text-destructive">
          {state.message ?? 'Apply failed.'}
        </p>
        <div className="flex gap-1.5 pt-1">
          <Button
            size="sm"
            variant="default"
            onClick={onAccept}
            data-testid="ai-action-retry"
          >
            Retry
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            data-testid="ai-action-dismiss"
          >
            Dismiss
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      <Button
        size="sm"
        variant="default"
        onClick={onAccept}
        disabled={state.status === 'applying'}
        data-testid="ai-action-accept"
        aria-label={`Apply suggestion for ${actionHeadline(action)}`}
      >
        {state.status === 'applying' ? (
          <Loader2 className="animate-spin" />
        ) : (
          <Check />
        )}
        Apply
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onReject}
        disabled={state.status === 'applying'}
        data-testid="ai-action-reject"
        aria-label={`Reject suggestion for ${actionHeadline(action)}`}
      >
        <X />
        Reject
      </Button>
    </div>
  )
}

function kindLabel(action: AiSuggestionAction): string {
  switch (action.kind) {
    case 'field-edit':
      return 'Field edit'
    case 'project-metadata':
      return 'Project setup'
    case 'project-task':
      return 'Project task'
  }
}
